/**
 * Bugs & Features board (MIS-109): the contract shared by the server routes
 * and the client page.
 *
 * Firms report bugs and request features. ClaimHive admins (users.is_super_admin)
 * triage them and publish chosen feature requests to a shared roadmap, where
 * every signed-in user can vote. Nothing on the board sends email, texts or
 * notifications.
 *
 * API (all under /api/feedback; 404 for every path while FEEDBACK_BOARD_ENABLED is not "true"):
 *   GET    /status                               → FeedbackBoardStatus
 *   GET    /items                                → { items: FeedbackItemSummary[] }   (the session's firm only)
 *   POST   /items                                → FeedbackItemDetail                  (multipart: fields + 0–3 "screenshots")
 *   GET    /items/:id                            → FeedbackItemDetail                  (own firm, or a ClaimHive admin)
 *   GET    /items/:id/screenshots/:screenshotId  → image bytes                         (own firm, or a ClaimHive admin)
 *   PATCH  /items/:id/status                     → FeedbackItemDetail                  (ClaimHive admin; not to or from "held")
 *   PATCH  /items/:id/hold                       → FeedbackItemDetail                  (ClaimHive admin, held items only: Approve or Reject)
 *   PUT    /items/:id/publication                → FeedbackItemDetail                  (ClaimHive admin, feature requests only)
 *   DELETE /items/:id/publication                → FeedbackItemDetail                  (ClaimHive admin)
 *   POST   /items/:id/vote                       → RoadmapVoteResult                   (published items only)
 *   GET    /roadmap                              → { items: RoadmapEntry[] }
 *   GET    /admin/items?type=&status=&firm=      → AdminFeedbackList                   (ClaimHive admin; type is the triaged type)
 *
 * Another firm's item is always a 404, never a 403.
 *
 * Auto-triage (MIS-169, FEEDBACK_AUTOTRIAGE_ENABLED=true): after an item is
 * saved, the server classifies it, links likely duplicates in the same firm,
 * files bugs in Linear and turns feature requests into unpublished roadmap
 * drafts. ClaimHive admin views carry the result in `triage`. Publishing stays
 * manual.
 *
 * Untrusted items (MIS-170) take a separate, injection-safe triage path and are
 * held, not filed, when anything looks off or FEEDBACK_PROD_AUTOFILE_ENABLED is
 * not "true". A ClaimHive admin approves (files it) or rejects (Won't do) each
 * held item. A firm sees a held item as "New".
 */
import { z } from 'zod';

export const FEEDBACK_API = '/api/feedback';
/** The board page. `?tab=admin` opens the Admin tab for ClaimHive admins. */
export const FEEDBACK_PAGE_PATH = '/feedback';
export const FEEDBACK_TABS = ['mine', 'roadmap', 'admin'] as const;
export type FeedbackTab = (typeof FEEDBACK_TABS)[number];
export const FEEDBACK_ADMIN_TAB_HREF = `${FEEDBACK_PAGE_PATH}?tab=admin`;

export const FEEDBACK_TYPES = ['bug', 'feature'] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

/** "held" (MIS-170): an untrusted item auto-triage did not file; only Approve or Reject moves it on. */
export const FEEDBACK_STATUSES = ['new', 'held', 'triaged', 'planned', 'in_progress', 'shipped', 'wont_do'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  held: 'Held',
  triaged: 'Triaged',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  wont_do: "Won't do",
};

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug',
  feature: 'Feature request',
};

export const VOTE_VALUES = ['up', 'down'] as const;
export type VoteValue = (typeof VOTE_VALUES)[number];

export const FEEDBACK_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type FeedbackSeverity = (typeof FEEDBACK_SEVERITIES)[number];

export const FEEDBACK_SEVERITY_LABELS: Record<FeedbackSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

/** How auto-triage decided: a model through the app's AI client, or keyword rules when no AI is configured. */
export type FeedbackTriageMethod = 'ai' | 'rules';

/**
 * Where a bug's Linear issue stands. "not_configured" is a dry run: the key or
 * team id is missing, so nothing was sent.
 */
export const LINEAR_FILING_STATES = ['pending', 'filed', 'not_configured', 'failed'] as const;
export type LinearFilingState = (typeof LINEAR_FILING_STATES)[number];

export const LINEAR_FILING_LABELS: Record<LinearFilingState, string> = {
  pending: 'Linear: filing',
  filed: 'Linear',
  not_configured: 'Linear: not configured',
  failed: 'Linear: failed',
};

/**
 * Trust tier (MIS-170). The server sets it on each item from the session and
 * the environment; a client can't send or change it. Trusted: submitted on QA
 * (any non-production environment) or by a verified missionae.com account.
 * Untrusted: anyone else on prod. ClaimHive admin views only.
 */
export const FEEDBACK_TRUST_TIERS = ['trusted', 'untrusted'] as const;
export type FeedbackTrustTier = (typeof FEEDBACK_TRUST_TIERS)[number];

export const FEEDBACK_TRUST_TIER_LABELS: Record<FeedbackTrustTier, string> = {
  trusted: 'Trusted',
  untrusted: 'Untrusted',
};

/**
 * Why auto-triage held an untrusted item instead of filing it (MIS-170):
 *   sanitized            the sanitizer removed something (a link, code, an instruction-like sentence, …)
 *   injection_suspected  the model thinks the text speaks to an AI or a system
 *   sensitive_area       permissions, billing, sign-in, data or another firm
 *   invalid_answer       the model call failed, or its answer did not match the fixed schema
 *   autofile_off         FEEDBACK_PROD_AUTOFILE_ENABLED is not "true"
 */
export const FEEDBACK_HOLD_REASONS = ['sanitized', 'injection_suspected', 'sensitive_area', 'invalid_answer', 'autofile_off'] as const;
export type FeedbackHoldReason = (typeof FEEDBACK_HOLD_REASONS)[number];

export const FEEDBACK_HOLD_REASON_LABELS: Record<FeedbackHoldReason, string> = {
  sanitized: 'Sanitizer removed content',
  injection_suspected: 'Possible prompt injection',
  sensitive_area: 'Sensitive area',
  invalid_answer: 'Model answer missing or invalid',
  autofile_off: 'Prod auto-filing is off',
};

export const FEEDBACK_SENSITIVE_AREAS = ['permissions', 'billing', 'auth', 'data', 'other_firms'] as const;
export type FeedbackSensitiveArea = (typeof FEEDBACK_SENSITIVE_AREAS)[number];

export const FEEDBACK_SENSITIVE_AREA_LABELS: Record<FeedbackSensitiveArea, string> = {
  permissions: 'Permissions',
  billing: 'Billing',
  auth: 'Sign-in and sessions',
  data: 'Data',
  other_firms: "Other firms' data",
};

/**
 * A status-history row is a status change, the one auto-triage entry, or a
 * ClaimHive admin's Approve or Reject of a held item (with their reason).
 */
export const FEEDBACK_HISTORY_KINDS = ['status', 'auto_triaged', 'hold_approved', 'hold_rejected'] as const;
export type FeedbackHistoryKind = (typeof FEEDBACK_HISTORY_KINDS)[number];

export const FEEDBACK_HOLD_DECISIONS = ['approve', 'reject'] as const;
export type FeedbackHoldDecision = (typeof FEEDBACK_HOLD_DECISIONS)[number];
export const FEEDBACK_HOLD_NOTE_MAX = 500;

/** The in-app acknowledgement a reporter gets the moment they submit a bug or feature report. */
export const FEEDBACK_REPORTER_ACK_MESSAGE = 'Received, our team is working on it.';

export const FEEDBACK_MAX_SCREENSHOTS = 3;
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;
/** What the file picker and the server accept. "image/jpg" is a non-standard alias some browsers send. */
export const FEEDBACK_SCREENSHOT_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'] as const;
export const FEEDBACK_SCREENSHOT_ACCEPT = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';

export const FEEDBACK_LIMITS = {
  title: 200,
  longText: 5000,
  pagePath: 2000,
  userAgent: 512,
  publicTitle: 200,
  publicSummary: 2000,
} as const;

const requiredText = (max: number) => z.string().trim().min(1, 'Required').max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value ? value : null));

/** Bug report fields. The server adds the user agent, the user and the firm itself. */
export const submitBugSchema = z.object({
  type: z.literal('bug'),
  title: requiredText(FEEDBACK_LIMITS.title),
  stepsToReproduce: requiredText(FEEDBACK_LIMITS.longText),
  expectedVsActual: optionalText(FEEDBACK_LIMITS.longText),
  pagePath: z.string().trim().max(FEEDBACK_LIMITS.pagePath).optional().default(''),
  viewportWidth: z.coerce.number().int().min(0).max(100_000).optional(),
  viewportHeight: z.coerce.number().int().min(0).max(100_000).optional(),
});

export const submitFeatureSchema = z.object({
  type: z.literal('feature'),
  title: requiredText(FEEDBACK_LIMITS.title),
  description: requiredText(FEEDBACK_LIMITS.longText),
});

export const submitFeedbackSchema = z.discriminatedUnion('type', [submitBugSchema, submitFeatureSchema]);
export type SubmitFeedbackInput = z.infer<typeof submitFeedbackSchema>;

export const changeStatusSchema = z.object({ status: z.enum(FEEDBACK_STATUSES) });

/** Approve or Reject a held item. The reason goes into status history. */
export const holdDecisionSchema = z.object({
  decision: z.enum(FEEDBACK_HOLD_DECISIONS),
  reason: requiredText(FEEDBACK_HOLD_NOTE_MAX),
});
export type HoldDecisionInput = z.infer<typeof holdDecisionSchema>;

export const publishSchema = z.object({
  publicTitle: requiredText(FEEDBACK_LIMITS.publicTitle),
  publicSummary: requiredText(FEEDBACK_LIMITS.publicSummary),
});
export type PublishInput = z.infer<typeof publishSchema>;

export const voteSchema = z.object({ value: z.enum(VOTE_VALUES) });

export const adminListQuerySchema = z.object({
  type: z.enum(FEEDBACK_TYPES).optional(),
  status: z.enum(FEEDBACK_STATUSES).optional(),
  /** Firm (organization) id to filter by. A filter for ClaimHive admins, never an identity. */
  firm: z.string().trim().min(1).max(100).optional(),
});
export type AdminListQuery = z.infer<typeof adminListQuerySchema>;

export interface FeedbackBoardStatus {
  enabled: true;
  isClaimHiveAdmin: boolean;
  /** False for a ClaimHive admin with no firm on their session: they can triage but not submit. */
  canSubmit: boolean;
}

export interface FeedbackContext {
  /** Path and query of the page the bug was reported from, with secret-looking query values redacted. */
  pagePath: string;
  userAgent: string;
  /** "390x844", or "" when the browser did not send it. */
  viewport: string;
}

export interface FeedbackScreenshotView {
  id: string;
  /** Same-origin route that checks the item's firm before it streams the image. */
  url: string;
  mimeType: string;
  name: string;
  size: number;
}

export interface FeedbackStatusChangeView {
  from: FeedbackStatus;
  to: FeedbackStatus;
  at: string;
  /** "auto_triaged" for the entry auto-triage writes ("Auto-triaged" in the UI). A firm never sees a hold kind or status. */
  kind: FeedbackHistoryKind;
  /** ClaimHive admins only. */
  changedByName?: string | null;
  /** ClaimHive admins only: the auto-triage result in one line, or the admin's reason for an Approve or Reject. */
  note?: string | null;
}

/** Why an untrusted item was held (MIS-170). Stays on the item after Approve or Reject. ClaimHive admin views only. */
export interface FeedbackHoldView {
  at: string;
  reasons: FeedbackHoldReason[];
  /** What the sanitizer removed ("url", "instruction", …). */
  sanitizeFlags: string[];
  sensitiveArea: FeedbackSensitiveArea | null;
}

export interface FeedbackLinearView {
  state: LinearFilingState;
  /** "MIS-123" once filed. */
  identifier: string | null;
  /** https://linear.app/… once filed. */
  url: string | null;
  /** Short reason when the filing failed. */
  error: string | null;
}

/** What auto-triage decided. ClaimHive admin views only. */
export interface FeedbackTriageView {
  at: string;
  method: FeedbackTriageMethod;
  /** What the reporter picked. */
  submittedType: FeedbackType;
  /** What triage settled on: the reporter's pick unless it was clearly wrong. */
  type: FeedbackType;
  severity: FeedbackSeverity;
  /** One line. */
  severityReason: string;
  /** Short clean title, with personal and claim details redacted. */
  title: string;
  /** The same firm's earlier item this one repeats; its reporter's vote went there. */
  duplicateOf: { id: string; title: string } | null;
  /** Bugs only, and not for duplicates. */
  linear: FeedbackLinearView | null;
  /** Feature requests only, and not for duplicates: prefills the publish form. Not on the roadmap until an admin publishes. */
  roadmapDraft: { publicTitle: string; publicSummary: string } | null;
}

/** The type triage settled on, else the reporter's pick. Publishing checks this. */
export function triagedFeedbackType(item: { type: FeedbackType; triage?: { type: FeedbackType } | null }): FeedbackType {
  return item.triage?.type ?? item.type;
}

export interface FeedbackItemSummary {
  id: string;
  type: FeedbackType;
  title: string;
  status: FeedbackStatus;
  createdAt: string;
  reportedByName: string | null;
  screenshotCount: number;
  published: boolean;
  /** Net votes while published; 0 when not published. */
  netVotes: number;
  /** ClaimHive admin views only. */
  orgId?: string;
  orgName?: string | null;
  /** ClaimHive admin views only: null until auto-triage has run on the item. */
  triage?: FeedbackTriageView | null;
  /** ClaimHive admin views only: later items auto-triage linked to this one as duplicates. */
  duplicateCount?: number;
  /** ClaimHive admin views only. Items saved before MIS-170 read as untrusted. */
  trustTier?: FeedbackTrustTier;
  /** ClaimHive admin views only: set when auto-triage held the item. */
  hold?: FeedbackHoldView | null;
  /**
   * ClaimHive admin views only: whether the report is approved to be worked on.
   * New reports are approved by default. Purely internal work tracking — it is
   * never part of the public roadmap and never affects what the public sees.
   */
  approvedForWork?: boolean;
}

export interface FeedbackItemDetail extends FeedbackItemSummary {
  stepsToReproduce: string | null;
  expectedVsActual: string | null;
  description: string | null;
  context: FeedbackContext | null;
  screenshots: FeedbackScreenshotView[];
  statusHistory: FeedbackStatusChangeView[];
  publication: { publicTitle: string; publicSummary: string; publishedAt: string } | null;
  reportedByEmail: string | null;
}

/** Everything the shared roadmap shows. No firm, user, description, screenshots, page or browser. */
export interface RoadmapEntry {
  id: string;
  publicTitle: string;
  publicSummary: string;
  status: FeedbackStatus;
  netVotes: number;
  myVote: VoteValue | null;
}

export interface RoadmapVoteResult {
  id: string;
  netVotes: number;
  myVote: VoteValue | null;
}

export interface AdminFeedbackList {
  items: FeedbackItemSummary[];
  /** Firms that have at least one item, for the firm filter. */
  firms: Array<{ id: string; name: string | null }>;
  /** Held items waiting for Approve or Reject, whatever the filters. The server always sends it. */
  heldCount?: number;
}

export const REDACTED = 'REDACTED';

/** Redact wherever they appear in a param name ("access_token", "X-Amz-Signature", "sessionid", "authorization"). */
const SECRET_SUBSTRING = /(token|secret|signature|password|passwd|credential|apikey|session|auth)/i;
/** Redact as a whole word of the name ("code_verifier", "api_key") or at its end ("passcode", "accesskey", "oauthCode"). */
const SECRET_WORDS = new Set(['code', 'key', 'sig', 'pwd', 'otp', 'jwt']);
const SECRET_SUFFIX = /(code|key|sig|pwd|otp|jwt)$/;
/** Ordinary names that end in "code" or "key"; compared with separators removed, so "postal_code" stays too. */
const SAFE_NAMES = new Set(['zipcode', 'postalcode', 'postcode', 'countrycode', 'statecode', 'areacode', 'languagecode', 'currencycode', 'sortkey']);

/**
 * True when a query param named like token, code, key, secret, signature or
 * password should have its value hidden. Errs toward hiding: "author" or
 * "monkey" are hidden too.
 */
export function isSecretParamName(name: string): boolean {
  const compact = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (SAFE_NAMES.has(compact)) return false;
  if (SECRET_SUBSTRING.test(name) || SECRET_SUFFIX.test(compact)) return true;
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/);
  return words.some((word) => SECRET_WORDS.has(word));
}

/**
 * Page path and query with the values of secret-looking query params replaced
 * by REDACTED. The hash is dropped. Accepts a path ("/claims?x=1") or a full URL;
 * only the path and query are kept. Auto-triage's outbound redaction (MIS-169)
 * builds on this in server/services/feedback-board/redaction.ts, which stays
 * out of the client bundle.
 */
export function redactPageUrl(input: string): string {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  let url: URL;
  try {
    url = new URL(raw, 'https://redact.invalid');
  } catch {
    return '';
  }
  const parts: string[] = [];
  url.searchParams.forEach((value, name) => {
    const kept = isSecretParamName(name) ? REDACTED : value;
    parts.push(`${encodeURIComponent(name)}=${encodeURIComponent(kept)}`);
  });
  const path = url.pathname.slice(0, FEEDBACK_LIMITS.pagePath);
  return parts.length ? `${path}?${parts.join('&')}`.slice(0, FEEDBACK_LIMITS.pagePath) : path;
}
